
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import AiAgentForm from "@/components/ai/AiAgentForm.vue";
import { listAiAgents, createAiAgent, updateAiAgent, deleteAiAgent } from "@/api/aiAgents.api.js";

export default defineComponent({
  name: "ai-agents",
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AiAgentForm
  },
  data() {
    return {
      loading: true,
      saving: false,
      loadError: "",
      agents: [] as any[],
      showModal: false,
      editingAgent: null as any
    };
  },
  async mounted() {
    await this.loadAgents();
  },
  methods: {
    async loadAgents() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await listAiAgents();
        this.agents = response.data?.agents || [];
      } catch (err) {
        this.loadError = (err as any)?.message || "Failed to load AI agents.";
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingAgent = null;
      this.showModal = true;
    },
    openEdit(agent: any) {
      try {
        this.editingAgent = JSON.parse(JSON.stringify(agent));
      } catch {
        this.editingAgent = { ...agent };
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingAgent = null;
    },
    isFollowUpEnabled(agent: any) {
      const scenarios = agent && agent.followUpScenarios;
      const stopped = scenarios && scenarios.contactStoppedReplying;
      return Boolean(stopped && stopped.enabled);
    },
    followUpStepCount(agent: any) {
      const scenarios = agent && agent.followUpScenarios;
      const stopped = scenarios && scenarios.contactStoppedReplying;
      const steps = stopped && stopped.steps;
      return Array.isArray(steps) ? steps.length : 0;
    },
    async saveAgent(payload: Record<string, unknown>) {
      this.saving = true;
      try {
        if (this.editingAgent?.id) {
          await updateAiAgent(this.editingAgent.id, payload);
        } else {
          await createAiAgent(payload);
        }
        await this.loadAgents();
        this.closeModal();
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "AI agent saved",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not save AI agent",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(agent: any) {
      const result = await Swal.fire({
        icon: "warning",
        title: `Delete "${agent.name}"?`,
        text: "This cannot be undone. Agents in use by auto-reply or posts cannot be deleted.",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33"
      });
      if (!result.isConfirmed) return;

      try {
        await deleteAiAgent(agent.id);
        await this.loadAgents();
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "AI agent deleted",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not delete AI agent",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
      }
    }
  }
});
